import { Controller } from "@hotwired/stimulus"

/**
 * Export Progress Controller
 *
 * Simple export flow:
 * 1. Click Export button -> Show format modal
 * 2. Choose format -> Start export, show progress
 * 3. When complete -> Show download button
 * 4. On error -> Show error with retry
 */
export default class extends Controller {
  static targets = [
    "exportButton",
    "progressContainer",
    "progressBar",
    "progressText",
    "downloadButton",
    "errorContainer",
    "errorMessage",
    "formatModal"
  ]

  static values = {
    exportUrl: String,
    pollUrl: String,
    pollInterval: { type: Number, default: 1500 },
    exportId: String
  }

  connect() {
    this.polling = false
    this.lastFormat = null
  }

  disconnect() {
    this.stopPolling()
  }

  // Show format selection modal
  showFormatModal(event) {
    event.preventDefault()
    if (this.hasFormatModalTarget) {
      this.formatModalTarget.classList.remove('hidden')
    }
  }

  // Hide format modal
  hideFormatModal() {
    if (this.hasFormatModalTarget) {
      this.formatModalTarget.classList.add('hidden')
    }
  }

  // Start export with selected format
  async startExport(event) {
    event.preventDefault()
    const format = event.currentTarget.dataset.format
    this.lastFormat = format

    this.hideFormatModal()
    this.showProgress()
    this.updateProgress(0, "Initialisation...")

    try {
      const response = await fetch(this.exportUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          format: format,
          ...this.collectFormParams()
        })
      })

      if (response.ok) {
        const data = await response.json()
        this.exportIdValue = data.export_id
        this.pollUrlValue = data.poll_url
        this.startPolling()
      } else {
        const error = await response.json()
        this.showError(error.message || "Erreur lors du lancement de l'export")
      }
    } catch (error) {
      console.error("Export error:", error)
      this.showError("Erreur de connexion au serveur")
    }
  }

  // Polling
  startPolling() {
    if (this.polling) return
    this.polling = true
    this.poll()
  }

  stopPolling() {
    this.polling = false
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  }

  async poll() {
    if (!this.polling || !this.pollUrlValue) return

    try {
      const response = await fetch(this.pollUrlValue, {
        headers: { 'Accept': 'application/json' }
      })

      if (response.ok) {
        const data = await response.json()
        this.handlePollResponse(data)
      } else {
        this.showError("Erreur lors de la vérification")
        this.stopPolling()
      }
    } catch (error) {
      this.showError("Erreur de connexion")
      this.stopPolling()
    }
  }

  handlePollResponse(data) {
    this.updateProgress(data.progress || 0, data.status_message || "Traitement...")

    switch (data.status) {
      case 'pending':
      case 'processing':
        this.pollTimer = setTimeout(() => this.poll(), this.pollIntervalValue)
        break

      case 'completed':
        this.stopPolling()
        this.showCompleted(data.download_url, data.filename)
        break

      case 'failed':
        this.stopPolling()
        this.showError(data.error_message || "L'export a échoué")
        break

      default:
        this.pollTimer = setTimeout(() => this.poll(), this.pollIntervalValue)
    }
  }

  // UI Updates
  showProgress() {
    this.hideError()
    this.hideDownload()
    if (this.hasExportButtonTarget) {
      this.exportButtonTarget.style.display = 'none'
    }
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove('hidden')
    }
  }

  hideProgress() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add('hidden')
    }
  }

  updateProgress(percent, message) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = message || `${Math.round(percent)}%`
    }
  }

  showCompleted(downloadUrl, filename) {
    this.hideProgress()
    if (this.hasExportButtonTarget) {
      this.exportButtonTarget.style.display = ''
    }
    if (this.hasDownloadButtonTarget && downloadUrl) {
      this.downloadButtonTarget.href = downloadUrl
      this.downloadButtonTarget.download = filename || 'export'
      this.downloadButtonTarget.style.display = ''
    }
  }

  showError(message) {
    this.hideProgress()
    if (this.hasExportButtonTarget) {
      this.exportButtonTarget.style.display = 'none'
    }
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.remove('hidden')
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
    }
  }

  hideError() {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.add('hidden')
    }
  }

  hideDownload() {
    if (this.hasDownloadButtonTarget) {
      this.downloadButtonTarget.style.display = 'none'
    }
  }

  // Retry after error
  retry() {
    this.hideError()
    if (this.hasExportButtonTarget) {
      this.exportButtonTarget.style.display = ''
    }
    // Optionally auto-open format modal
    this.showFormatModal({ preventDefault: () => {} })
  }

  // Collect form params
  collectFormParams() {
    const form = document.querySelector('form[action*="balance_sheets"]')
    if (!form) return {}

    const formData = new FormData(form)
    const params = {}

    for (const [key, value] of formData.entries()) {
      if (value && value.trim() !== '') {
        const match = key.match(/q\[(\w+)\]/)
        if (match) {
          params[match[1]] = value
        }
      }
    }
    return params
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
